import type { ProposalModelConstraint } from '@/legacy-api/proposals-slice-types'
import type { ConstraintValidation } from './constraints'

export type ComplianceCheck =
  | WeightCompliance
  | MarketValueCompliance
  | ModelCompliance

export enum ComplianceCheckType {
  WeightCompliance = 'weight_compliance',
  MarketValueCompliance = 'market_value_compliance',
  ModelCompliance = 'model_compliance',
}

interface BaseCompliance {
  type: ComplianceCheckType
  sleeve_id: string
  compliant: boolean
}

export interface WeightCompliance extends BaseCompliance {
  type: ComplianceCheckType.WeightCompliance
  min_weight: number
  target_weight: number
  max_weight: number
  current_weight: number
}

export interface MarketValueCompliance extends BaseCompliance {
  type: ComplianceCheckType.MarketValueCompliance
  min_market_value: number
  target_market_value: number
  max_market_value: number
  current_market_value: number
}

// TODO: We should revisit how we are setting up the constraints.
// Since this is being used both for sleeves and proposals, we should consider
// creating a new type for each.
//
// Current constraint usage patterns:
// 1. Sleeve customizations (SleeveCustomizations):
//    - Used to validate model customizations applied to a sleeve
//    - Each constraint_id maps to a ModelConstraintValidation
//
// 2. Proposal constraints (ProposalModelConstraint):
//    - Contains specific constraint types for different validation purposes:
//      - Universe constraints: municipal, sp_rating, sp_outlook, close_ytw, close_price, private_placement
//      - Average value constraints: sp_rating_average
//      - Policy constraints: ladder_policy
//
// 3. Model constraints (shared foundation):
//    - Both sleeve and proposal constraints are based on model-level constraints
//    - The same ModelCompliance interface is used for different contexts
//
// Future refactoring considerations:
// - Create separate interfaces for SleeveModelCompliance and ProposalModelCompliance
// - Extract common validation patterns into shared utility functions
// - Have some sort of backward compatibility once changes are implemented
export interface ModelCompliance extends BaseCompliance {
  type: ComplianceCheckType.ModelCompliance
  model_id: string
  constraints: SleeveCustomizations | ProposalModelConstraint
}

interface SleeveCustomizations {
  [constraint_id: string]: ConstraintValidation
}
